import { motion, useScroll, useTransform } from 'framer-motion'
import { Zap, Wifi, Tv, ChevronRight, Star, Shield, Clock, PiggyBank, ChevronDown, Check, ArrowRight, Mail, Users, TrendingDown, Award, ThumbsUp, MessageCircle, Sparkles, Target, Heart, Calendar } from 'lucide-react'
import { useState, useRef, useEffect } from 'react'
import { Link } from 'react-router-dom'
import SEO from '../components/SEO'
import { useContactModal } from '../components/ContactModalContext'

const services = [
  {
    icon: Zap,
    title: 'Luce e Gas',
    description: 'Confronta le migliori offerte luce e gas e risparmia fino a 400 euro all\'anno sulla bolletta',
    href: 'https://compare.bollettaamica.com/home/energia',
    color: 'from-yellow-400 to-orange-500',
    video: '/videos/luce-gas.mp4',
    benefits: ['Risparmio garantito', 'Cambio senza interruzioni', 'Assistenza dedicata'],
  },
  {
    icon: Wifi,
    title: 'Fibra Internet',
    description: 'Trova la connessione internet più veloce al miglior prezzo per la tua zona',
    href: 'https://compare.bollettaamica.com/home/fibra',
    color: 'from-blue-400 to-cyan-500',
    video: '/videos/fibra.mp4',
    benefits: ['Velocità fino a 2.5 Gbps', 'Copertura verificata', 'Attivazione rapida'],
  },
  {
    icon: Tv,
    title: 'Streaming',
    description: 'Scopri le piattaforme di streaming e i pacchetti più convenienti per tutta la famiglia',
    href: 'https://compare.bollettaamica.com/home/streaming',
    color: 'from-purple-400 to-pink-500',
    video: '/videos/streaming.mp4',
    benefits: ['Netflix, Disney+, Prime', 'Pacchetti combinati', 'Prova gratuita'],
  },
]

const steps = [
  {
    number: '01',
    title: 'Inserisci i tuoi dati',
    description: 'Dicci quanto consumi o carica una bolletta. Bastano pochi secondi!',
    image: '/carletto/pc 1.png',
  },
  {
    number: '02',
    title: 'Confronta le offerte',
    description: 'Ti mostriamo tutte le tariffe disponibili ordinate per convenienza.',
    image: '/carletto/12 1.png',
  },
  {
    number: '03',
    title: 'Scegli e attiva',
    description: 'Seleziona l\'offerta migliore e completa l\'attivazione online in pochi click.',
    image: '/carletto/3.png',
  },
  {
    number: '04',
    title: 'Inizia a risparmiare',
    description: 'Il cambio avviene senza interruzioni. Tu non devi fare nulla!',
    image: '/carletto/13 1.png',
  },
]

const features = [
  {
    icon: PiggyBank,
    title: 'Risparmio Garantito',
    description: 'Confrontiamo centinaia di offerte per trovare quella più conveniente per te. In media i nostri utenti risparmiano il 30%.',
  },
  {
    icon: Shield,
    title: '100% Gratuito e Sicuro',
    description: 'Il nostro servizio è completamente gratuito e senza impegno. I tuoi dati sono protetti e non vengono mai condivisi.',
  },
  {
    icon: Clock,
    title: 'Veloce e Semplice',
    description: 'In soli 2 minuti trovi l\'offerta perfetta per le tue esigenze. Niente burocrazia, niente complicazioni.',
  },
  {
    icon: Star,
    title: 'Assistenza Dedicata',
    description: 'Il nostro team di esperti ti accompagna in ogni fase del cambio fornitore, dalla scelta all\'attivazione.',
  },
  {
    icon: Check,
    title: 'Fornitori Verificati',
    description: 'Collaboriamo solo con fornitori autorizzati ARERA. Tutte le offerte sono ufficiali e trasparenti.',
  },
  {
    icon: TrendingDown,
    title: 'Prezzi Sempre Aggiornati',
    description: 'Il nostro database viene aggiornato quotidianamente per garantirti sempre le tariffe più recenti.',
  },
]

const testimonials = [
  {
    name: 'Marco R.',
    location: 'Milano',
    text: 'Grazie a Bolletta Amica ho risparmiato oltre 300€ all\'anno sulla bolletta del gas. Servizio eccellente!',
    rating: 5,
    savings: '€320/anno',
  },
  {
    name: 'Giulia M.',
    location: 'Roma',
    text: 'Finalmente un comparatore che funziona davvero. Ho trovato un\'offerta fibra incredibile in pochi minuti.',
    rating: 5,
    savings: '€180/anno',
  },
  {
    name: 'Alessandro P.',
    location: 'Napoli',
    text: 'Carletto mi ha guidato passo passo. Il cambio fornitore è stato semplicissimo e senza interruzioni.',
    rating: 5,
    savings: '€250/anno',
  },
  {
    name: 'Francesca L.',
    location: 'Torino',
    text: 'Ero scettica ma mi sono dovuta ricredere. Risparmio reale e assistenza sempre disponibile.',
    rating: 5,
    savings: '€410/anno',
  },
]

const faqs = [
  {
    question: 'Come funziona il servizio di confronto?',
    answer: 'Il nostro comparatore analizza le offerte di tutti i principali fornitori italiani e ti mostra quelle più adatte alle tue esigenze. Inserisci i tuoi consumi e in pochi secondi avrai una panoramica completa delle tariffe disponibili.',
  },
  {
    question: 'Il servizio è davvero gratuito?',
    answer: 'Sì, il nostro servizio è completamente gratuito per gli utenti. Non ci sono costi nascosti né commissioni. Guadagniamo solo se decidi di attivare un\'offerta tramite noi, direttamente dai fornitori.',
  },
  {
    question: 'Quanto tempo ci vuole per cambiare fornitore?',
    answer: 'Il cambio fornitore è molto semplice e non richiede alcun intervento tecnico. Una volta scelta l\'offerta, il nuovo fornitore si occuperà di tutto. Il passaggio avviene senza interruzione del servizio, generalmente entro 30-60 giorni.',
  },
  {
    question: 'Posso fidarmi delle offerte proposte?',
    answer: 'Assolutamente sì. Collaboriamo solo con fornitori autorizzati e verificati da ARERA. Tutte le offerte mostrate sono ufficiali e i prezzi sono sempre aggiornati quotidianamente.',
  },
  {
    question: 'Cosa succede se ho problemi dopo il cambio?',
    answer: 'Il nostro servizio di assistenza è sempre disponibile per aiutarti. Puoi contattarci via email e ti supporteremo nella risoluzione di qualsiasi problema con il nuovo fornitore.',
  },
  {
    question: 'Devo disdire il vecchio contratto?',
    answer: 'No, non devi fare nulla! Sarà il nuovo fornitore a occuparsi di tutto, inclusa la comunicazione al vecchio gestore. Tu dovrai solo aspettare il passaggio automatico.',
  },
]

const trustIndicators = [
  { value: '50.000+', label: 'Clienti soddisfatti', icon: Users },
  { value: '€2.5M', label: 'Risparmiati dai nostri utenti', icon: PiggyBank },
  { value: '4.8/5', label: 'Valutazione media', icon: Star },
  { value: '100+', label: 'Fornitori confrontati', icon: Target },
]

// Partners with logo images - all new logos with uniform display
const partnersWithLogos = [
  { name: 'Enel', logo: '/partners/enel.png' },
  { name: 'TIM', logo: '/partners/tim.png' },
  { name: 'Fastweb', logo: '/partners/fastweb.png' },
  { name: 'Fastweb Vodafone', logo: '/partners/fastweb-vodafone.png' },
  { name: 'Sky', logo: '/partners/sky.png' },
  { name: 'Sky WiFi', logo: '/partners/sky-wifi.png' },
  { name: 'Sentra', logo: '/partners/sentra.png' },
  { name: 'Luce e Gas Italia', logo: '/partners/luce_gas_italia.png' },
  { name: 'Edison', logo: '/partners/edison.png' },
  { name: 'Plenitude', logo: '/partners/plenitude.png' },
  { name: 'AGN', logo: '/partners/agn.svg' },
  { name: 'Netflix', logo: '/partners/netflix.png' },
  { name: 'Disney+', logo: '/partners/disney+.svg' },
]

interface BlogPost {
  id: string
  slug: string
  title: string
  excerpt: string
  image: string
  category: string
  date: string
  readTime: number
}

export default function Home() {
  const [openFaq, setOpenFaq] = useState<number | null>(null)
  const [posts, setPosts] = useState<BlogPost[]>([])
  const [loadingPosts, setLoadingPosts] = useState(true)
  const heroRef = useRef<HTMLDivElement>(null)
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"]
  })
  const heroY = useTransform(scrollYProgress, [0, 1], [0, 200])
  const heroOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0])
  const { openModal } = useContactModal()

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })
  }

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const response = await fetch('/api/posts')
        const data = await response.json()
        setPosts(data.slice(0, 3)) // Get only first 3 posts
      } catch (error) {
        console.error('Error fetching posts:', error)
      } finally {
        setLoadingPosts(false)
      }
    }
    fetchPosts()
  }, [])

  return (
    <div className="overflow-hidden">
      <SEO
        title="Confronta Tariffe Luce, Gas e Fibra"
        description="Confronta gratuitamente le migliori offerte di luce, gas, fibra e streaming. Risparmia fino a 400 euro all'anno sulla bolletta con Bolletta Amica!"
        canonical="/"
        keywords="confronto tariffe, luce e gas, fibra internet, risparmio bollette, comparatore energia, offerte luce, offerte gas, tariffe fibra, streaming"
        faqs={faqs}
        includeServiceSchema={true}
        includeRatingSchema={true}
      />
      {/* Hero Section */}
      <section ref={heroRef} className="relative min-h-screen flex items-center pt-20 overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-white via-primary/5 to-white" />
          <div className="absolute inset-0 pattern-dots opacity-40" />
          <motion.div 
            className="absolute top-20 right-0 w-[800px] h-[800px] bg-primary/10 rounded-full blur-3xl"
            animate={{ 
              scale: [1, 1.2, 1],
              opacity: [0.3, 0.5, 0.3],
            }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div 
            className="absolute -bottom-40 -left-40 w-[600px] h-[600px] bg-blue-400/10 rounded-full blur-3xl"
            animate={{ 
              scale: [1.2, 1, 1.2],
              opacity: [0.2, 0.4, 0.2],
            }}
            transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>
        
        <motion.div style={{ y: heroY, opacity: heroOpacity }} className="container-custom relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 backdrop-blur-sm border border-primary/20 text-primary-700 font-semibold rounded-full text-sm mb-6"
              >
                <Sparkles className="w-4 h-4" />
                Il comparatore #1 in Italia
              </motion.div>
              
              <h1 className="heading-xl mb-6">
                Risparmia sulle tue{' '}
                <span className="text-primary relative">
                  bollette
                  <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 200 12" fill="none">
                    <path d="M2 10C50 4 150 4 198 10" stroke="#F8B120" strokeWidth="4" strokeLinecap="round"/>
                  </svg>
                </span>
                {' '}con Carletto
              </h1>
              
              <p className="text-xl text-dark-500 mb-8 max-w-lg">
                Confronta <span className="font-semibold text-dark-700">gratuitamente</span> le migliori offerte di luce, gas, fibra e streaming. 
                Trova l'offerta perfetta per te in pochi click!
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <a
                  href="https://compare.bollettaamica.com/home/energia"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary text-lg px-8 py-4 shadow-lg shadow-primary/30 hover:shadow-xl hover:shadow-primary/40 transition-all"
                >
                  Confronta Ora
                  <ChevronRight className="w-5 h-5" />
                </a>
                <a
                  href="#come-funziona"
                  className="btn-outline text-lg px-8 py-4"
                >
                  Come Funziona?
                </a>
              </div>

              {/* Mini Stats */}
              <div className="flex flex-wrap gap-6">
                <div className="flex items-center gap-2">
                  <div className="flex -space-x-2">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <div key={i} className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-primary-600 border-2 border-white flex items-center justify-center text-white text-xs font-bold">
                        {['M', 'G', 'A', 'F', 'L'][i-1]}
                      </div>
                    ))}
                  </div>
                  <span className="text-sm text-dark-500">+50k utenti</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Star key={i} className="w-4 h-4 text-primary fill-primary" />
                    ))}
                  </div>
                  <span className="text-sm text-dark-500">4.89/5 (Trustpilot)</span>
                </div>
              </div>
            </motion.div>

            {/* Right - Carletto */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative lg:h-[600px]"
            >
              <div className="relative h-full flex items-center justify-center">
                {/* Animated Rings */}
                <motion.div 
                  className="absolute w-[400px] h-[400px] border-2 border-primary/20 rounded-full"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                />
                <motion.div 
                  className="absolute w-[500px] h-[500px] border border-primary/10 rounded-full"
                  animate={{ rotate: -360 }}
                  transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
                />
                
                {/* Glow Effect */}
                <div className="absolute w-80 h-80 bg-primary/20 blur-3xl rounded-full" />
                
                {/* Carletto Image */}
                <motion.img
                  src="/carletto/3.png"
                  alt="Carletto - La mascotte di Bolletta Amica"
                  className="relative z-10 w-full max-w-md mx-auto drop-shadow-2xl"
                  animate={{ y: [0, -20, 0] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                />

                {/* Floating Cards */}
                <motion.div
                  initial={{ opacity: 0, x: -50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.8 }}
                  className="absolute top-20 -left-4 lg:left-0"
                >
                  <motion.div 
                    className="bg-white/90 backdrop-blur-xl rounded-2xl shadow-xl p-4 flex items-center gap-3 border border-white/50"
                    animate={{ y: [0, -10, 0] }}
                    transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
                  >
                    <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                      <TrendingDown className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <p className="font-bold text-dark-800">-35%</p>
                      <p className="text-sm text-dark-500">sulla bolletta</p>
                    </div>
                  </motion.div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 1 }}
                  className="absolute top-40 -right-4 lg:right-0"
                >
                  <motion.div 
                    className="bg-white/90 backdrop-blur-xl rounded-2xl shadow-xl p-4 flex items-center gap-3 border border-white/50"
                    animate={{ y: [0, 10, 0] }}
                    transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
                  >
                    <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                      <Zap className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-bold text-dark-800">100+</p>
                      <p className="text-sm text-dark-500">fornitori</p>
                    </div>
                  </motion.div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.2 }}
                  className="absolute bottom-10 left-10"
                >
                  <motion.div 
                    className="bg-white/90 backdrop-blur-xl rounded-2xl shadow-xl p-4 flex items-center gap-3 border border-white/50"
                    animate={{ y: [0, -8, 0] }}
                    transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                  >
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <Shield className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <p className="font-bold text-dark-800">100%</p>
                      <p className="text-sm text-dark-500">gratuito</p>
                    </div>
                  </motion.div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <a href="#servizi" className="flex flex-col items-center text-dark-400 hover:text-primary transition-colors group">
            <span className="text-sm mb-2 group-hover:text-primary">Scopri di più</span>
            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            >
              <ChevronDown className="w-6 h-6" />
            </motion.div>
          </a>
        </motion.div>
      </section>

      {/* Ticker / Partners */}
      <section className="py-8 bg-dark-50 border-y border-dark-100 overflow-hidden">
        <div className="container-custom mb-4">
          <p className="text-center text-sm text-dark-400 font-medium uppercase tracking-wider">Partners Commerciali</p>
        </div>
        <div className="marquee-container">
          <div className="marquee-content">
            {partnersWithLogos.map((partner, index) => (
              <div key={index} className="flex-shrink-0 h-12 w-32 flex items-center justify-center">
                <img 
                  src={partner.logo} 
                  alt={partner.name}
                  className="max-h-full max-w-full object-contain grayscale hover:grayscale-0 opacity-70 hover:opacity-100 transition-all duration-300"
                />
              </div>
            ))}
            {/* Duplicate for seamless loop */}
            {partnersWithLogos.map((partner, index) => (
              <div key={`dup-${index}`} className="flex-shrink-0 h-12 w-32 flex items-center justify-center">
                <img 
                  src={partner.logo} 
                  alt={partner.name}
                  className="max-h-full max-w-full object-contain grayscale hover:grayscale-0 opacity-70 hover:opacity-100 transition-all duration-300"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section - Expanded */}
      <section id="servizi" className="section-padding">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="inline-block px-4 py-2 bg-primary/10 text-primary-700 font-semibold rounded-full text-sm mb-4">
              I Nostri Servizi
            </span>
            <h2 className="heading-lg mb-4">Confronta e risparmia su tutto</h2>
            <p className="text-xl text-dark-500 max-w-2xl mx-auto">
              Un unico portale per confrontare le migliori offerte per la tua casa
            </p>
          </motion.div>

          <div className="space-y-24">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                className={`grid lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
              >
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className={`inline-flex items-center gap-2 w-14 h-14 rounded-2xl bg-gradient-to-br ${service.color} justify-center mb-6`}>
                    <service.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-3xl md:text-4xl font-display font-bold mb-4">{service.title}</h3>
                  <p className="text-xl text-dark-500 mb-6">{service.description}</p>
                  
                  <ul className="space-y-3 mb-8">
                    {service.benefits.map((benefit) => (
                      <li key={benefit} className="flex items-center gap-3">
                        <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center">
                          <Check className="w-4 h-4 text-green-600" />
                        </div>
                        <span className="text-dark-600">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <a
                    href={service.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary inline-flex"
                  >
                    Confronta {service.title}
                    <ArrowRight className="w-5 h-5" />
                  </a>
                </div>
                
                <div className={`relative ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                  <div className={`absolute inset-0 bg-gradient-to-br ${service.color} opacity-10 blur-3xl rounded-full scale-75`} />
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    className="relative bg-gradient-to-br from-white to-dark-50 rounded-3xl shadow-xl border border-dark-100 overflow-hidden aspect-square"
                  >
                    <video
                      src={service.video}
                      autoPlay
                      loop
                      muted
                      playsInline
                      className="w-full h-full object-cover scale-110"
                    />
                  </motion.div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Indicators - Compact Bar */}
      <section className="py-4 bg-dark-800 border-y border-dark-700">
        <div className="container-custom">
          <div className="flex flex-wrap justify-center items-center gap-x-8 gap-y-3 md:gap-x-12">
            {trustIndicators.map((item, index) => (
              <div
                key={item.label}
                className="flex items-center gap-2 text-white/90"
              >
                <item.icon className="w-4 h-4 text-primary" />
                <span className="font-bold">{item.value}</span>
                <span className="text-white/60 text-sm">{item.label}</span>
                {index < trustIndicators.length - 1 && (
                  <span className="hidden md:inline text-dark-600 ml-6">|</span>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="come-funziona" className="section-padding bg-dark-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="inline-block px-4 py-2 bg-primary/10 text-primary-700 font-semibold rounded-full text-sm mb-4">
              Semplicissimo
            </span>
            <h2 className="heading-lg mb-4">Come Funziona</h2>
            <p className="text-xl text-dark-500 max-w-2xl mx-auto">
              In 4 semplici passaggi inizi a risparmiare. Carletto ti guida!
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                className="relative"
              >
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-24 left-full w-full h-0.5 bg-gradient-to-r from-primary to-transparent z-0" />
                )}
                <div className="card p-6 relative z-10 h-full">
                  <span className="text-6xl font-display font-bold text-primary/20 absolute top-4 right-4">
                    {step.number}
                  </span>
                  <div className="h-32 flex items-center justify-center mb-4">
                    <img src={step.image} alt={step.title} className="h-full w-auto object-contain" />
                  </div>
                  <h3 className="text-xl font-display font-bold mb-2">{step.title}</h3>
                  <p className="text-dark-500">{step.description}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <a
              href="https://compare.bollettaamica.com/home/energia"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary text-lg px-10 py-4"
            >
              Inizia Ora - È Gratis!
              <ChevronRight className="w-5 h-5" />
            </a>
          </motion.div>
        </div>
      </section>

      {/* Features Section - Grid */}
      <section id="chi-siamo" className="section-padding">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="inline-block px-4 py-2 bg-primary/10 text-primary-700 font-semibold rounded-full text-sm mb-4">
                Perché Noi
              </span>
              <h2 className="heading-lg mb-6">
                Perché scegliere{' '}
                <span className="text-primary">Bolletta Amica</span>?
              </h2>
              <p className="text-xl text-dark-500 mb-8">
                Da anni aiutiamo migliaia di famiglie italiane a risparmiare sulle bollette. 
                Il nostro comparatore è semplice, veloce e completamente gratuito.
              </p>

              <div className="flex items-center gap-4 p-4 bg-green-50 rounded-2xl border border-green-100">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                  <Award className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="font-bold text-dark-800">Comparatore dell'Anno 2025</p>
                  <p className="text-sm text-dark-500">Premio Innovazione Digitale</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="absolute inset-0 bg-primary/10 blur-3xl rounded-full scale-75" />
              <img
                src="/carletto/2b 1.png"
                alt="Carletto"
                className="relative z-10 w-full max-w-md mx-auto"
              />
            </motion.div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card p-6 hover:shadow-xl transition-shadow"
              >
                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
                  <feature.icon className="w-7 h-7 text-primary" />
                </div>
                <h3 className="text-xl font-display font-bold mb-2">{feature.title}</h3>
                <p className="text-dark-500">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section-padding bg-dark-800 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-96 h-96 bg-primary/20 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl" />
        </div>
        
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="inline-block px-4 py-2 bg-white/10 text-white font-semibold rounded-full text-sm mb-4">
              Recensioni
            </span>
            <h2 className="heading-lg text-white mb-4">Cosa dicono di noi</h2>
            <p className="text-xl text-dark-300 max-w-2xl mx-auto">
              Migliaia di clienti soddisfatti. Ecco le loro storie.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white/10 backdrop-blur-xl rounded-2xl p-6 border border-white/10"
              >
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-primary fill-primary" />
                  ))}
                </div>
                <p className="text-white/90 mb-4">"{testimonial.text}"</p>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-white">{testimonial.name}</p>
                    <p className="text-sm text-white/60">{testimonial.location}</p>
                  </div>
                  <div className="px-3 py-1 bg-green-500/20 rounded-full">
                    <span className="text-green-400 font-semibold text-sm">{testimonial.savings}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <div className="inline-flex items-center gap-2 text-white/60">
              <ThumbsUp className="w-5 h-5" />
              <span>Oltre 2.400 recensioni verificate</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Trustpilot Section */}
      <section className="section-padding bg-gradient-to-b from-white to-dark-50 overflow-hidden">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left - Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-[#00b67a] rounded-xl flex items-center justify-center">
                  <Star className="w-6 h-6 text-white fill-white" />
                </div>
                <div>
                  <p className="text-sm text-dark-500 font-medium">Valutati su</p>
                  <p className="text-xl font-bold text-dark-800">Trustpilot</p>
                </div>
              </div>
              
              <h2 className="heading-lg mb-4">
                La fiducia dei nostri clienti è la nostra{' '}
                <span className="text-[#00b67a]">priorità</span>
              </h2>
              
               <p className="text-xl text-dark-500 mb-8">
                 Siamo orgogliosi delle recensioni che i nostri clienti ci lasciano. 
                 Ogni giorno lavoriamo per offrire il miglior servizio possibile.
               </p>
               
               {/* Rating Display */}
               <div className="inline-block">
                 <div className="bg-white rounded-2xl shadow-lg p-6 mb-4">
                   <div className="flex items-center gap-4">
                     <div className="text-5xl font-bold text-dark-800">4.9</div>
                     <div>
                       <div className="flex gap-1">
                         {[1, 2, 3, 4, 5].map((i) => (
                           <div key={i} className={`w-8 h-8 ${i <= 4 ? 'bg-[#00b67a]' : 'bg-[#73cf11]'} flex items-center justify-center`}>
                             <Star className="w-5 h-5 text-white fill-white" />
                           </div>
                         ))}
                       </div>
                     </div>
                   </div>
                 </div>
                 
                 <a
                   href="https://it.trustpilot.com/review/bollettaamica.com"
                   target="_blank"
                   rel="noopener noreferrer"
                   className="btn-outline inline-flex items-center gap-2 border-[#00b67a] text-[#00b67a] hover:bg-[#00b67a] hover:text-white"
                 >
                   Leggi tutte le recensioni
                   <ArrowRight className="w-5 h-5" />
                 </a>
               </div>
            </motion.div>
            
            {/* Right - Review Cards */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="absolute inset-0 bg-[#00b67a]/10 blur-3xl rounded-full scale-75" />
              
              <div className="relative space-y-4">
                {/* Review 1 */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1 }}
                  className="bg-white rounded-2xl shadow-lg p-5 ml-0 lg:ml-12"
                >
                  <div className="flex gap-1 mb-2">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Star key={i} className="w-4 h-4 text-[#00b67a] fill-[#00b67a]" />
                    ))}
                  </div>
                  <p className="text-dark-700 font-medium mb-2">"Servizio eccezionale!"</p>
                  <p className="text-sm text-dark-500 mb-3">Ho risparmiato oltre 300€ in un anno grazie ai loro consigli. Super consigliato!</p>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-dark-600">Marco R.</span>
                    <span className="text-xs text-dark-400">Verificato</span>
                  </div>
                </motion.div>
                
                {/* Review 2 */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 }}
                  className="bg-white rounded-2xl shadow-lg p-5 mr-0 lg:mr-12"
                >
                  <div className="flex gap-1 mb-2">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Star key={i} className="w-4 h-4 text-[#00b67a] fill-[#00b67a]" />
                    ))}
                  </div>
                  <p className="text-dark-700 font-medium mb-2">"Finalmente un comparatore serio"</p>
                  <p className="text-sm text-dark-500 mb-3">Trasparenza e professionalità. Ho cambiato fornitore in 5 minuti!</p>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-dark-600">Laura B.</span>
                    <span className="text-xs text-dark-400">Verificato</span>
                  </div>
                </motion.div>
                
                {/* Review 3 */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3 }}
                  className="bg-white rounded-2xl shadow-lg p-5 ml-0 lg:ml-8"
                >
                  <div className="flex gap-1 mb-2">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Star key={i} className="w-4 h-4 text-[#00b67a] fill-[#00b67a]" />
                    ))}
                  </div>
                  <p className="text-dark-700 font-medium mb-2">"Assistenza top"</p>
                  <p className="text-sm text-dark-500 mb-3">Team sempre disponibile. Mi hanno seguito passo passo nel cambio.</p>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-dark-600">Giuseppe T.</span>
                    <span className="text-xs text-dark-400">Verificato</span>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Carletto Section - Fun */}
      <section className="section-padding overflow-hidden">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-2 lg:order-1"
            >
              <div className="grid grid-cols-2 gap-4">
                <motion.div 
                  whileHover={{ scale: 1.05, rotate: -2 }}
                  className="bg-gradient-to-br from-primary/10 to-primary/5 rounded-3xl p-4"
                >
                  <img src="/carletto/5.png" alt="Carletto" className="w-full" />
                </motion.div>
                <motion.div 
                  whileHover={{ scale: 1.05, rotate: 2 }}
                  className="bg-gradient-to-br from-blue-100 to-blue-50 rounded-3xl p-4 mt-8"
                >
                  <img src="/carletto/8.png" alt="Carletto" className="w-full" />
                </motion.div>
                <motion.div 
                  whileHover={{ scale: 1.05, rotate: 2 }}
                  className="bg-gradient-to-br from-purple-100 to-purple-50 rounded-3xl p-4 -mt-8"
                >
                  <img src="/carletto/9.png" alt="Carletto" className="w-full" />
                </motion.div>
                <motion.div 
                  whileHover={{ scale: 1.05, rotate: -2 }}
                  className="bg-gradient-to-br from-green-100 to-green-50 rounded-3xl p-4"
                >
                  <img src="/carletto/11 1 1.png" alt="Carletto" className="w-full" />
                </motion.div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-1 lg:order-2"
            >
              <span className="inline-block px-4 py-2 bg-primary/10 text-primary-700 font-semibold rounded-full text-sm mb-4">
                Il Tuo Amico
              </span>
              <h2 className="heading-lg mb-6">
                Ciao, sono <span className="text-primary">Carletto!</span>
              </h2>
              <p className="text-xl text-dark-500 mb-6">
                Sono qui per aiutarti a risparmiare sulle bollette! Con me confrontare 
                le offerte è semplice, veloce e persino divertente.
              </p>
              <p className="text-lg text-dark-500 mb-8">
                Non ti preoccupare della burocrazia, ci penso io a tutto. Tu rilassati 
                e goditi il risparmio!
              </p>
              
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Heart className="w-5 h-5 text-primary" />
                  </div>
                  <span className="text-dark-600">Sempre dalla tua parte</span>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <MessageCircle className="w-5 h-5 text-primary" />
                  </div>
                  <span className="text-dark-600">Assistenza umana e disponibile</span>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-primary" />
                  </div>
                  <span className="text-dark-600">Zero stress, solo risparmio</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Blog Preview */}
      <section className="section-padding bg-dark-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12"
          >
            <div>
              <span className="inline-block px-4 py-2 bg-primary/10 text-primary-700 font-semibold rounded-full text-sm mb-4">
                Blog
              </span>
              <h2 className="heading-lg">Il Blog di Carletto</h2>
            </div>
            <Link to="/il-blog-di-carletto" className="btn-outline">
              Vedi tutti gli articoli
              <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>

          {loadingPosts ? (
            <div className="grid md:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="card p-6 animate-pulse">
                  <div className="bg-dark-100 h-48 rounded-xl mb-4" />
                  <div className="bg-dark-100 h-6 rounded mb-3" />
                  <div className="bg-dark-100 h-4 w-3/4 rounded" />
                </div>
              ))}
            </div>
          ) : posts.length > 0 ? (
            <div className="grid md:grid-cols-3 gap-8">
              {posts.map((post, index) => (
                <motion.article
                  key={post.id || post.slug}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link to={`/blog/${post.slug}`} className="card block group overflow-hidden">
                    {/* Image */}
                    <div className="relative h-48 bg-primary/10 overflow-hidden">
                      <img
                        src={post.image}
                        alt={post.title}
                        className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-300"
                      />
                      <span className="absolute top-4 left-4 px-3 py-1 bg-white/90 backdrop-blur-sm rounded-full text-sm font-medium text-dark-700">
                        {post.category}
                      </span>
                    </div>

                    {/* Content */}
                    <div className="p-6">
                      <h3 className="text-xl font-display font-bold mb-3 group-hover:text-primary transition-colors line-clamp-2">
                        {post.title}
                      </h3>
                      <p className="text-dark-500 mb-4 line-clamp-2">{post.excerpt}</p>

                      {/* Meta */}
                      <div className="flex items-center justify-between text-sm text-dark-400">
                        <div className="flex items-center gap-4">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {formatDate(post.date)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {post.readTime} min
                          </span>
                        </div>
                      </div>

                      {/* Read More */}
                      <div className="mt-4 pt-4 border-t border-dark-100">
                        <span className="inline-flex items-center text-primary font-semibold group-hover:gap-2 transition-all">
                          Leggi di più
                          <ChevronRight className="w-4 h-4" />
                        </span>
                      </div>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-16"
            >
              <img
                src="/carletto/9.png"
                alt="Carletto"
                className="w-48 h-auto mx-auto mb-6"
              />
              <h3 className="text-2xl font-display font-bold mb-2">Torna presto!</h3>
              <p className="text-dark-500">
                Stiamo preparando tanti nuovi articoli per te. Scopri tutte le novità!
              </p>
            </motion.div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-br from-dark-800 via-dark-900 to-dark-800 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/30 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl" />
          <div className="absolute inset-0 bg-[url('/pattern.png')] opacity-5" />
        </div>
        
        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="heading-lg text-white mb-6">
                Pronto a risparmiare sulle tue bollette?
              </h2>
              <p className="text-xl text-dark-300 mb-8">
                Inizia subito il confronto gratuito e scopri quanto puoi risparmiare. 
                In media i nostri utenti risparmiano <span className="text-primary font-bold">€320 all'anno</span>!
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="https://compare.bollettaamica.com/home/energia"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary text-lg px-8 py-4"
                >
                  Confronta Ora - È Gratis!
                  <ChevronRight className="w-5 h-5" />
                </a>
              </div>

              <button 
                onClick={() => openModal()}
                className="mt-8 flex items-center gap-3 text-white/70 hover:text-primary transition-colors"
              >
                <Mail className="w-5 h-5 text-primary" />
                <span>Contattaci</span>
              </button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img
                src="/carletto/13 1.png"
                alt="Carletto felice"
                className="w-full max-w-md mx-auto"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="section-padding">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="inline-block px-4 py-2 bg-primary/10 text-primary-700 font-semibold rounded-full text-sm mb-4">
                FAQ
              </span>
              <h2 className="heading-lg mb-6">Domande Frequenti</h2>
              <p className="text-xl text-dark-500 mb-8">
                Hai domande? Carletto ha le risposte! Se non trovi quello che cerchi, 
                contattaci e saremo felici di aiutarti.
              </p>
              
              <div className="bg-primary/10 rounded-2xl p-6">
                <img 
                  src="/carletto/ChatGPT Image 8 apr 2025, 09_55_30.png" 
                  alt="Carletto risponde" 
                  className="w-48 mx-auto mb-4"
                />
                <p className="text-center text-dark-600">
                  Non hai trovato risposta? <br/>
                  <button 
                    onClick={() => openModal('Domanda FAQ')}
                    className="text-primary font-semibold hover:underline"
                  >
                    Scrivici!
                  </button>
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    className="card overflow-hidden"
                  >
                    <button
                      onClick={() => setOpenFaq(openFaq === index ? null : index)}
                      className="w-full p-6 flex items-center justify-between text-left"
                    >
                      <span className="text-lg font-semibold text-dark-800 pr-4">
                        {faq.question}
                      </span>
                      <div className={`w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 transition-transform ${openFaq === index ? 'rotate-180' : ''}`}>
                        <ChevronDown className="w-5 h-5 text-primary" />
                      </div>
                    </button>
                    <motion.div
                      initial={false}
                      animate={{
                        height: openFaq === index ? 'auto' : 0,
                        opacity: openFaq === index ? 1 : 0,
                      }}
                      className="overflow-hidden"
                    >
                      <p className="px-6 pb-6 text-dark-500">{faq.answer}</p>
                    </motion.div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Add marquee animation styles */}
      <style>{`
        .marquee-container {
          overflow: hidden;
          width: 100%;
        }
        .marquee-content {
          display: flex;
          align-items: center;
          gap: 4rem;
          animation: marquee 30s linear infinite;
          width: max-content;
        }
        .marquee-content:hover {
          animation-play-state: paused;
        }
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
    </div>
  )
}
